<template>
  <teleport to="body">
    <div v-if="isFullSize" class="code-editor-wrapper full-size">
      <div class="code-editor-toggler">
        <div class="full-size-header">{{ label }}</div>
        <div style="display: flex; gap: .5rem;">
          <i class="fa fa-compress" @click="toggleFullSize" v-tooltip="`Smaller editor`"/>
          <i v-if="disabled" class="fa fa-lock"/>
        </div>
      </div>
      <AceEditor
        v-model:codeContent="value"
        v-model:editor="editor"
        :options="{
          ...options,
          enableBasicAutocompletion: [staticWordCompleter]
        }"
        :theme="theme"
        :lang="language"
        :width="width"
        :height="'calc(100% - 55px)'"
        @keydown.esc="toggleFullSize"
        :style="disabled ? 'background-color: var(--surface-100);' : ''"
      />
    </div>
  </teleport>

  <div v-if="!isFullSize" class="code-editor-wrapper">
    <div class="code-editor-toggler">
      <label :class="{ 'p-error': invalid }" v-if="!hideLabel">{{ label }}</label>
      <div v-else class="full-size-header"></div>
      <div style="display: flex; gap: .5rem;">
        <i v-if="showMaximize" class="fa fa-expand" @click="toggleFullSize" v-tooltip="`Enlarge editor`"/>
        <i v-if="disabled" class="fa fa-lock"/>
      </div>
    </div>
    <AceEditor
      v-model:codeContent="value"
      v-model:editor="editor"
      :options="{
        ...options,
        enableBasicAutocompletion: [staticWordCompleter]
      }"
      :theme="theme"
      :lang="language"
      :width="width"
      :height="height"
      :style="disabled ? 'background-color: var(--surface-100);' : ''"
    />
  </div>

  <template v-if="error && showErrors">
    <small v-if="typeof error === 'string'" class="p-error">{{ error }}</small>
    <template v-else-if="typeof error === 'object' && error.length">
      <template v-for="errorObj in error">
        <small class="p-error">{{ errorObj.$message }}</small>
      </template>
    </template>
  </template>
</template>

<script>
import autocompleteItems from '@/assets/autocomplete.json';
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { removeCircular } from '@/js/functions';

export default {
  name: "CodeInput",
  components: {
    AceEditor: defineAsyncComponent(async () => {
      const module = await import('ace-editor-vue3');
      await import('brace/mode/javascript');
      await import('brace/mode/css');
      await import('brace/mode/json');
      await import('brace/mode/scss');
      await import('brace/mode/pgsql');
      await import('brace/mode/text');
      await import('brace/mode/html');
      await import('brace/theme/github');
      await import('brace/theme/dracula');
      await import('brace/ext/language_tools');
      await import('brace/ext/searchbox');
      return module;
    })
  },
  props: {
    modelValue: String,
    label: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    error: {
      default: null
    },
    hideLabel: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default: function () {
        return {
          showPrintMargin: false
        }
      }
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '200px'
    },
    language: {
      type: String,
      default: 'javascript'
    },
    defaultValue: {
      required: false
    },
    invalid: {
      type: Boolean,
      default: false
    },
    showErrors: {
      type: Boolean,
      default: true
    },
    autocompleteKeys: {
      type: Array,
      default: []
    },
    showMaximize: {
      type: Boolean,
      default: true
    },
    localFormInstance: {
      type: Object
    }
  },
  emits: ['update:modelValue'],
  computed: {
    value: {
      get() {
        return typeof this.modelValue === 'object' ? JSON.stringify(this.modelValue) : this.modelValue || ''
      },
      set(value) {
        this.$emit('update:modelValue', String(value))
      }
    },
    theme () {
      const html = document.querySelector('html');
      if (html.classList.contains('dark')) {
        return 'dracula';
      }
      return 'github';
    },
    ...mapGetters({
      formEdit: 'GET_FORM_EDIT'
    })
  },
  mounted() {
    this.setEditorReadOnly();
  },
  data(){
    return {
      editor: null,
      isFullSize: false,
      staticWordCompleter: {
        getCompletions: (editor, session, pos, prefix, callback) => {
          const wordsList = this.getAutocompleteItems();
          const getInstanceSuggestions = () => {
            if (!this.localFormInstance) return [];
            const line = session.getLine(pos.row);
            const beforeCursor = line.slice(0, pos.column);
            const hasTrailingDot = beforeCursor.endsWith(".");
            const match = beforeCursor.match(/([\w$][\w\d$]*(?:\.[\w$][\w\d$]*)*)\.?$/);
            const contextPath = match ? match[1] : "";
            const suggestions = [];
            const used = new Set();

            // Helper: returns accessor notation for a key.
            // It prepends a dot only if the key is a valid identifier and if needed.
            const safeKey = (key, prefixDot = false) => {
              if (/^[A-Za-z_$][\w$]*$/.test(key)) {
                return prefixDot ? `.${key}` : key;
              }
              return `['${key.replace(/'/g, "\\'")}']`;
            };

            // Recursively build a map of object paths.
            const getPaths = (obj, base = "") => {
              const result = {};
              for (const key in obj) {
                const path = base ? `${base}.${key}` : key;
                const value = obj[key];
                result[path] =
                  typeof value === "object" && value !== null && !Array.isArray(value)
                    ? 'form object'
                    : 'form ' + typeof value;

                if (typeof value === "object" && value !== null && !Array.isArray(value)) {
                  Object.assign(result, getPaths(value, path));
                }
              }
              return result;
            };

            const completionsObj = removeCircular({
              this: {
                formInstance: {
                  data: this.localFormInstance.data,
                  auxData: this.localFormInstance.auxData,
                  extraData: this.localFormInstance.extraData
                }
              }
            });

            if (this.localFormInstance.isMainDocument) {
              completionsObj.this = removeCircular({
                ...completionsObj.this,
                data: this.localFormInstance.data,
                auxData: this.localFormInstance.auxData,
                extraData: this.localFormInstance.extraData
              });
            } else if (this.localFormInstance.isBackendProcess) {
              completionsObj.this = removeCircular({
                formInstance: {
                  data: this.localFormInstance.data,
                  auxData: this.localFormInstance.auxData,
                  REQ: this.localFormInstance.REQ,
                  BODY: this.localFormInstance.BODY,
                  RESULT: this.localFormInstance.RESULT,
                  ERRORS: this.localFormInstance.ERRORS
                },
                data: this.localFormInstance.data,
                auxData: this.localFormInstance.auxData,
                REQ: this.localFormInstance.REQ,
                BODY: this.localFormInstance.BODY,
                RESULT: this.localFormInstance.RESULT,
                ERRORS: this.localFormInstance.ERRORS
              });
            } else if (this.formEdit.passedProps && this.formEdit.selectedFormId) {
              Object.keys(this.formEdit.passedProps).forEach(key => {
                completionsObj.this[key] = this.formEdit.passedProps[key];
              });
            }

            const pathMap = getPaths(completionsObj);

            for (const fullPath in pathMap) {
              if (
                contextPath &&
                (fullPath === contextPath || fullPath.startsWith(contextPath + "."))
              ) {
                const remaining = fullPath.slice(contextPath.length + 1);
                const nextKey = remaining.split(".")[0];
                if (nextKey && !used.has(nextKey)) {
                  used.add(nextKey);
                  suggestions.push({
                    caption: nextKey,
                    // If there's no trailing dot, prepend one (when valid).
                    value: safeKey(nextKey, !hasTrailingDot),
                    // Set the replacementPrefix to the context without the extra dot.
                    replacementPrefix: contextPath,
                    meta: pathMap[`${contextPath}.${nextKey}`] || "property"
                  });
                }
              } else if (!contextPath && !fullPath.includes(".")) {
                suggestions.push({
                  caption: fullPath,
                  value: safeKey(fullPath),
                  meta: pathMap[fullPath]
                });
              }
            }
            return suggestions;
          };
          const instanceSuggestions = getInstanceSuggestions();
          callback(null, [
            ...instanceSuggestions,
            ...wordsList.map(function(word) {
              return {
                docHTML: word.doc,
                caption: word.word,
                value: word.value ? word.value : word.word,
                meta: word.meta ? word.meta : "static"
              };
            }),
            ...session.$mode.$highlightRules.$keywordList.map(function(word) {
              return {
                caption: word,
                value: word,
                meta: 'keyword',
              };
            })
          ]);
        }
      }
    }
  },
  methods: {
    setEditorReadOnly() {
      if (this.editor) this.editor.setReadOnly(this.disabled);
    },
    getAutocompleteItems() {
      const items = []
      if (this.autocompleteKeys.length) {
        this.autocompleteKeys.forEach(key => {
          Object.keys(autocompleteItems).forEach(auxKey => {
            const auxKeys = auxKey.split(',');
            if (auxKeys.includes(key)) {
              items.push(...autocompleteItems[auxKey]);
            }
          });
        });
      }
      return items;
    },
    toggleFullSize(){
      this.isFullSize = !this.isFullSize
      setTimeout(() => {
        this.editor.setReadOnly(this.disabled);
      }, 50);
    }
  },
  watch: {
    editor(newVal, oldVal) {
      if (!oldVal && newVal) {
        newVal.commands.on('afterExec', function (e) {
          if (e.command.name === "insertstring" && e.args === ".") {
            newVal.execCommand("startAutocomplete");
          }
        });
      }
      this.setEditorReadOnly();
    },
    disabled(newVal) {
      if (this.editor) this.editor.setReadOnly(newVal);
    }
  }
}
</script>

<style scoped lang="scss">
:global(.ace_editor) {
  border-radius: 5px;
  border: 1px solid #eee;
}
:global(.ace_text-input) {
  position:absolute!important;
}
:global(.ace_autocomplete .ace_scroller .ace_content) {
  background-color: var(--surface-50) !important;
  color: var(--text-color) !important;
}
:global(.ace_editor.ace_autocomplete .ace_marker-layer .ace_active-line) {
  background-color: var(--surface-100) !important;
}
:global(.ace_tooltip) {
  background-color: var(--surface-100) !important;
  color: var(--text-color) !important;
}
:global(.ace_completion-highlight) {
  color: var(--blue-500) !important;
}
:global(.ace-dracula .ace_marker-layer .ace_selection) {
  background-color: var(--blue-500) !important;
}
.code-editor-wrapper{
  .code-editor-toggler{
    display: flex;
    align-items: center;
    justify-content: space-between;
    .fa{
      cursor: pointer;
    }
    label{
      margin-bottom: 5px;
    }
  }
  .full-size-header{
    visibility: hidden;
    font-weight: bold;
  }
  &.full-size{
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    z-index: 999999999;
    .code-editor-toggler{
      padding: 10px 20px;
      background: white;
      font-size: larger;
      border-bottom: 2px solid #ccc;
      height: 50px;
    }
    .full-size-header{
      visibility: visible;
    }
    .ace_editor {
      border-radius: 0;
      border: none;
    }
  }
}

html.dark{
  .code-editor-wrapper{
    &.full-size{
      .code-editor-toggler{
        padding: 10px 20px;
        background: #252636;
        font-size: larger;
        border-bottom: 2px solid #313249;
        height: 50px;
      }
    }
  }
}
</style>
